/**
 * Sticky bottom bar shown during active recording.
 *
 * Displays a pulsing red recording dot, elapsed timer,
 * real-time audio-reactive waveform visualization, Cancel link, and Finish button.
 * Uses live frequency data from Web Audio API AnalyserNode.
 * @module features/voice-report/components/RecordingBar/RecordingBarView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Props for RecordingBarView
 */
interface RecordingBarViewProps {
  /** Elapsed recording time in seconds */
  readonly elapsedSeconds: number;
  /** Live transcript from speech recognition (kept for hook compatibility) */
  readonly liveTranscript: string;
  /** Real-time frequency levels from Web Audio API (0-1 range) */
  readonly audioLevels: readonly number[];
  /** Called when Cancel is pressed */
  readonly onCancel: () => void;
  /** Called when Finish is pressed */
  readonly onFinish: () => void;
  /** Bottom safe-area inset to avoid home-indicator/gesture-bar overlap (SE-4618) */
  readonly bottomSafeArea: number;
}

/** Minimum bar height in pixels so silent frequencies remain visible */
const MIN_BAR_HEIGHT = 2;

/** Maximum bar height multiplier applied to normalized 0-1 audio levels */
const BAR_HEIGHT_SCALE = 20;

/**
 * Formats seconds as M:SS string.
 * @param seconds - Total elapsed seconds
 * @returns Formatted time string
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

/**
 * Converts a normalized audio level (0-1) to a pixel height for a waveform bar.
 * @param level - Normalized frequency value between 0 and 1
 * @returns Height in pixels (minimum 2px)
 */
const levelToHeight = (level: number): number =>
  Math.max(MIN_BAR_HEIGHT, level * BAR_HEIGHT_SCALE);

/**
 * Builds the bar container's bottom padding style. The safe-area bottom
 * inset is a measured runtime value with no closed token, so it is applied
 * through the container atom's runtime escape hatch (min 12px).
 * @param bottomSafeArea - Safe-area bottom inset in pixels
 * @returns The style object applying the bottom padding (min 12px)
 */
const buildBarContainerStyle = (bottomSafeArea: number) => ({
  paddingBottom: Math.max(bottomSafeArea, 12),
});

/**
 * Builds a single waveform bar's height style. The height is audio-reactive
 * (a per-frame runtime value with no closed sizing token), so it is applied
 * through the bar atom's runtime escape hatch.
 * @param level - Normalized frequency value between 0 and 1
 * @returns The style object setting the bar height in pixels
 */
const buildBarStyle = (level: number) => ({
  height: levelToHeight(level),
});

/**
 * Recording bar with timer, audio-reactive waveform, cancel, and finish controls.
 * @param props - Component props
 * @param props.elapsedSeconds - Timer value
 * @param props.liveTranscript - Live transcript (unused visually)
 * @param props.audioLevels - Real-time frequency levels for waveform
 * @param props.onCancel - Cancel handler
 * @param props.onFinish - Finish handler
 * @param props.bottomSafeArea - Bottom safe-area inset (px) supplied by Container; avoids home-indicator overlap (SE-4618)
 */
const RecordingBarView = ({
  elapsedSeconds,
  audioLevels,
  onCancel,
  onFinish,
  bottomSafeArea,
}: RecordingBarViewProps) => (
  <Box
    className="absolute inset-x-0 bottom-0 border-t border-outline-default bg-white px-4 pt-3"
    // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: measured safe-area bottom inset (bottomSafeArea) has no closed spacing token
    UNSAFE_style={buildBarContainerStyle(bottomSafeArea)}
  >
    <Stack direction="horizontal" className="items-center justify-center gap-4">
      {/* Recording indicator + timer */}
      <Stack direction="horizontal" className="items-center gap-2">
        <Box className="size-2 rounded-full bg-needs-semantic-error-600" />
        <Text variant="title" className="text-needs-semantic-error-600">
          {formatTime(elapsedSeconds)}
        </Text>
      </Stack>

      {/* Audio-reactive waveform visualization */}
      <Stack direction="horizontal" className="items-center gap-px">
        {audioLevels.map((level, i) => (
          <Box
            key={i}
            className="w-micro rounded-full bg-status-error"
            // eslint-disable-next-line no-restricted-syntax -- RFC-#6 genuine-runtime: per-frame audio-reactive waveform bar height (buildBarStyle(level)) has no closed sizing token
            UNSAFE_style={buildBarStyle(level)}
          />
        ))}
      </Stack>

      {/* Cancel */}
      <Pressable onPress={onCancel}>
        <Text variant="body" className="text-content-secondary">
          Cancel
        </Text>
      </Pressable>

      {/* Finish button */}
      <Button
        size="sm"
        onPress={onFinish}
        className="rounded-none bg-needs-semantic-error-600"
        label="Finish"
        labelVariant="body"
        labelTone="on-accent"
      />
    </Stack>
  </Box>
);

RecordingBarView.displayName = "RecordingBarView";

export default memo(RecordingBarView);
